/* eslint react-hooks/preserve-manual-memoization: ["warn"] */
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import styled from "styled-components";

/* Global Types & Enums */
import { TeamStatus } from "@coworker/apprestructured/src/shared/types/team";

/* Global Hooks */
import { useTranslation } from "@coworker/locales";
import { useActiveStoreUsers } from "@coworker/apprestructured/src/shared/hooks/users/useActiveStoreUsers";
import {
  useCountryId,
  useMyStore,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import { useGetStoresByCountry } from "@coworker/app/src/core/hooks/useStoresByCountry";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { useUserHasRoleOnPlatformOwner } from "../../login/hooks/useUserHasRoleOnPlatformOwner";
import { useAtkomstCoreStores } from "../../login/hooks/useAtkomstCoreStores";

/* Teams Management Hooks */
import useTeamsFilters from "../hooks/filters/useTeamsFilters";
import useFilteredTeams from "../hooks/useFilteredTeams";
import useFilteredUsers from "../hooks/useFilteredUsers";
import useTeamsWithUsersCount from "../hooks/useTeamsWithUsersCount";

/* Global Components */
import FixaSearch from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FilterPanel } from "@coworker/apprestructured/src/shared/filters/components/FilterPanel";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";

/* Teams Management Components */
import Tables from "./Tables/Tables";
import AddTeamModal from "./TeamModal/AddTeamModal";
import StoreSwitcher from "./StoreSwitcher";
import useHasTeamEditorPermissions from "../hooks/useTeamEditorPermissions";
import UnauthorizedPageAccordion from "@coworker/apprestructured/src/login/components/Unauthorized/UnauthorizedPageAccordion";

const ActionsSection = styled.div`
  padding: 32px 64px 32px 64px;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  @media (max-width: 768px) {
    padding: 16px 8px 16px 8px;
  }
`;

const TableFilters = styled.div`
  padding: 0 64px;
  display: grid;
  grid-template-columns: 3fr 1fr;
  @media (max-width: 768px) {
    display: block;
    padding: 0 8px;
  }
`;

const InputWrapper = styled.div`
  margin-top: 16px;
  width: 100%;
`;

const TeamsAndUsersOverview = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToastNotification();
  const [activeTableIndex, setActiveTableIndex] = useState(0);

  const country = useCountryId();
  // TO-DO: COAPP-12902 Remove country-store fallback when teams management uses only Åtkomst stores.
  const { data: stores, isLoading: isLoadingStores } =
    useGetStoresByCountry(country);
  // TO-DO: COAPP-12902 Remove flag gate when we fully migrate to Åtkomst stores.
  const enableAtkomstStoresInTeamsManagement = useFlag(FLAGS.ENABLE_ATKOMST);
  const {
    data: hasPlatformOwnerAuthorization,
    isLoading: isLoadingPlatformOwnerAuthorization,
  } = useUserHasRoleOnPlatformOwner();
  const { data: atkomstStores, isLoading: isLoadingAtkomstStores } =
    useAtkomstCoreStores(
      !isLoadingPlatformOwnerAuthorization && !!hasPlatformOwnerAuthorization
    );

  const defaultStore = useMyStore();
  const params = useParams();
  const routeStoreId = useMemo(() => {
    const value = params["storeId"];
    if (!value) return "";

    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }, [params]);

  const switcherStores = useMemo(() => {
    if (enableAtkomstStoresInTeamsManagement) {
      return atkomstStores || [];
    }

    // TO-DO: COAPP-12902 Remove fallback store source when only Åtkomst stores are used.
    return stores || [];
  }, [enableAtkomstStoresInTeamsManagement, atkomstStores, stores]);

  // TO-DO: COAPP-12902 Remove fallback loading branch when only Åtkomst stores are used.
  const isLoadingSwitcherStores = enableAtkomstStoresInTeamsManagement
    ? isLoadingAtkomstStores
    : isLoadingStores;

  const activeStore = useMemo(() => {
    if (isLoadingSwitcherStores) return defaultStore;
    return (
      switcherStores.find((store) => store.storeId === routeStoreId) ||
      defaultStore
    );
  }, [switcherStores, routeStoreId, defaultStore, isLoadingSwitcherStores]);

  const {
    data: storeTeams,
    isLoading: isLoadingTeams,
    isFetching: isFetchingTeams,
  } = useTeamsWithUsersCount(activeStore.storeId);

  const {
    data: storeUsers,
    isLoading: isLoadingUsers,
    isFetching: isFetchingUsers,
  } = useActiveStoreUsers(activeStore.storeId);

  const { filterValues, setFilterValues, useFiltersFactory, resetFilters } =
    useTeamsFilters(storeUsers || [], !!activeTableIndex);
  const [teamsSearchQuery, setTeamsSearchQuery] = useState("");
  const [usersSearchQuery, setUsersSearchQuery] = useState("");

  const filteredTeam = useMemo(() => {
    const selectedTeamId = filterValues.find(
      ({ filterId }) => filterId === "teamSelect"
    )?.value;

    if (!selectedTeamId) return undefined;

    const selectedTeam = storeTeams?.find((team) => team.id === selectedTeamId);

    return selectedTeam;
  }, [filterValues, isLoadingTeams, isFetchingTeams]);

  const filteredTeams = useFilteredTeams(
    storeTeams || [],
    filterValues,
    teamsSearchQuery
  );

  const filteredUsers = useFilteredUsers(
    storeUsers || [],
    storeTeams || [],
    filterValues,
    isLoadingUsers,
    filteredTeam?.id,
    usersSearchQuery
  );

  const hasActiveProductQualityTeam = useMemo(() => {
    return (
      filteredTeams.filter(
        (team) => team.is_product_quality && team.status === TeamStatus.ACTIVE
      ).length > 0
    );
  }, [filteredTeams]);

  const [addTeamModalOpen, setAddTeamModalOpen] = useState(false);

  const clearAllFilters = () => {
    resetFilters();
    setTeamsSearchQuery("");
    setUsersSearchQuery("");
  };

  const handleSwitchStore = (storeId: string) => {
    clearAllFilters();
    navigate("/teams/store/" + encodeURIComponent(storeId));
    const newStoreName = switcherStores.find(
      ({ storeId: id }) => id === storeId
    )?.name;
    showToast(
      t("switchedStoreToastMessageString", {
        oldStore: activeStore.name,
        newStore: newStoreName,
      })
    );
  };

  const handleTeamsRowClick = (teamId: string) => {
    navigate(
      `/teams/store/${encodeURIComponent(activeStore.storeId)}/team/${encodeURIComponent(teamId)}`
    );
  };

  const changeTab = (index: number) => {
    setActiveTableIndex(index);
  };

  const { hasPermission: isUserAuthorized, isLoading: isLoadingAuthorization } =
    useHasTeamEditorPermissions(activeStore.storeId || "");
  if (!isUserAuthorized && !isLoadingAuthorization)
    return (
      <div>
        <Linkbar currentLabel={t("teamManagementString")} />
        <UnauthorizedPageAccordion
          roleTypes={["storeSuperUser", "countrySuperUser"]}
          title={t("unauthorizedTeamsManagementTitleString")}
          description={t("unauthorizedTeamsManagementDescriptionString")}
        />
      </div>
    );

  return (
    <div>
      <Linkbar currentLabel={t("teamManagementString")} />

      <ActionsSection>
        <StoreSwitcher
          labelKey="selectStoreString"
          activeStore={activeStore}
          stores={switcherStores}
          loading={isLoadingSwitcherStores}
          handleSwitchStore={handleSwitchStore}
        />
        <FixaButton
          onClick={() => setAddTeamModalOpen(true)}
          text={t("addTeamString")}
          size="medium"
          type="primary"
        />
      </ActionsSection>

      <TableFilters>
        <div>
          <FilterPanel
            filterValues={filterValues}
            setFilterValues={setFilterValues}
            useFiltersFactory={useFiltersFactory}
            feature="teamsAndUsersOverview"
          />
        </div>
        <InputWrapper>
          {filteredTeam || !!activeTableIndex ? (
            <FixaSearch
              id="search"
              onClear={() => setUsersSearchQuery("")}
              placeholder={t("searchUsersString")}
              value={usersSearchQuery}
              onChange={(e) => setUsersSearchQuery(e.currentTarget.value)}
            />
          ) : (
            <FixaSearch
              id="search"
              onClear={() => setTeamsSearchQuery("")}
              placeholder={t("searchTeamString")}
              value={teamsSearchQuery}
              onChange={(e) => setTeamsSearchQuery(e.currentTarget.value)}
            />
          )}
        </InputWrapper>
      </TableFilters>

      <Tables
        users={filteredUsers}
        selectedTeam={filteredTeam}
        teams={filteredTeams}
        resetFilters={clearAllFilters}
        isLoadingTeams={isFetchingTeams || isLoadingTeams}
        isLoadingUsers={isFetchingUsers || isLoadingUsers}
        handleTeamsRowClick={handleTeamsRowClick}
        storeName={activeStore.name}
        activeTab={activeTableIndex}
        changeTab={changeTab}
      />

      <AddTeamModal
        visible={addTeamModalOpen}
        close={() => setAddTeamModalOpen(false)}
        storeId={activeStore.storeId}
        hasActiveProductQualityTeam={hasActiveProductQualityTeam}
      />
    </div>
  );
};

export default TeamsAndUsersOverview;
